import React from 'react';
import { Box, IconButton, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';

export const MIN_COUNT = 1;
export const MAX_COUNT = 20;

// The −/value/+ pill that sets how many relatives one click adds.
//
// ISOLATED ON PURPOSE, and imported by RelativeTile ALONE. The steppers are
// inherited from the desktop app and there is no evidence anyone uses them; if
// they are ever dropped, that should be one commit. Two rules keep it that way,
// and both are asserted by a test:
//
//   1. The pending count lives in RelativeTile local state. It is never lifted
//      into the container and never reaches memberRecords. If you find yourself
//      threading a count through the panel, the design has gone wrong.
//   2. This overlays the tile — position absolute — so it does NOT occupy the
//      grid flow. Removing it cannot move anything.
//
// Note they are NOT redundant today: decision D1 keeps the save gate, so a run of
// clicks is impossible and the stepper is the only way to add several at once.
// Do not describe it as removable in the current release.
const CountStepper = ({ value, onChange, disabled, visible, label }) => {
  const clamp = (n) => Math.min(Math.max(n, MIN_COUNT), MAX_COUNT);

  return (
    <Box
      // Overlays the tile rather than sitting in its flow — see rule 2 above.
      sx={{
        position: 'absolute',
        top: 2,
        right: 2,
        display: 'flex',
        alignItems: 'center',
        borderRadius: 10,
        bgcolor: 'background.paper',
        border: 1,
        borderColor: 'divider',
        // Hover AND focus-visible: hover alone is unreachable by keyboard. Always
        // shown on a coarse pointer, where there is no hover at all, and kept on
        // once the count is above 1 so a pinned value is never invisible.
        opacity: visible ? 1 : 0,
        pointerEvents: visible ? 'auto' : 'none',
        transition: 'opacity 120ms ease',
        '@media (pointer: coarse)': { opacity: 1, pointerEvents: 'auto' }
      }}
    >
      <IconButton
        size="small"
        aria-label={`Decrease count for ${label}`}
        disabled={disabled || value <= MIN_COUNT}
        onClick={(e) => {
          e.stopPropagation();
          onChange(clamp(value - 1));
        }}
        sx={{ p: 0.25 }}
      >
        <RemoveIcon sx={{ fontSize: 14 }} />
      </IconButton>
      <Typography
        aria-live="polite"
        sx={{
          fontSize: 12,
          fontWeight: 700,
          minWidth: 16,
          textAlign: 'center',
          color: 'text.primary'
        }}
      >
        {value}
      </Typography>
      <IconButton
        size="small"
        aria-label={`Increase count for ${label}`}
        disabled={disabled || value >= MAX_COUNT}
        onClick={(e) => {
          e.stopPropagation();
          onChange(clamp(value + 1));
        }}
        sx={{ p: 0.25 }}
      >
        <AddIcon sx={{ fontSize: 14 }} />
      </IconButton>
    </Box>
  );
};

export default CountStepper;
